<template>
  <tutorial-layout
    title="Mixins"
    :referenceLinks="referenceLinks"
    :youtubeLinks="youtubeLinks"
  >
    <p slot="overview">
      Mixins for VueJS add a great way to share functionality between your
      components. Traditionally, a developer would create a helper class to do
      some of the common work. Mixins add another tool to your toolbox when you
      need similar functionality within a VueJS component.
    </p>

    <p slot="benefits">
      <ul>
        <li>
          With Vue mixins, you can easily adhere to the DRY principle in programming which simply ensures that you do not repeat yourself
        </li>
        <li>
          With Vue mixins, you also get a great option of flexibility, a mixin object contains options for Vue components so there is a mixing of both mixin and component options
        </li>
        <li>
          Vue mixins are also safe, they do not affect changes outside their defined scope if they are well written
        </li>
        <li>
          They are a great platform for code reusability
        </li>
      </ul>
    </p>

    <h2>Mixin Syntax</h2>
    <br />
    <v-card>
      <v-card-text>
        <p>The Vue.js mixin syntax would look like the following code:</p>
        <pre>
          <code>// define a mixin object  
var myMixin = {  
  created: function() {  
    this.hello();  
  },  
  methods: {  
    hello: function() {  
      console.log("hello from mixin!");  
    }  
  }  
};  
// define a component that uses this mixin  
var Component = Vue.extend({  
  mixins: [myMixin]  
});  
var component = new Component(); // => "hello from mixin!"  
          </code>
        </pre>
      </v-card-text>
    </v-card>
<br /><br />
    <h2>Usuage of Mixins</h2>
    <br />
    <p>
      To use mixins in a Vue application, we have to pass through four stages
    </p>
    <ul>
      <li>Create the mixin file</li>
      <li>Import the mixin file into the needed component</li>
      <li>Remove the repeated logic from said components</li>
      <li>Register the mixin</li>
    </ul>
    <br /><br />


    <v-card>
      <v-card-title>1. Creating the mixin file</v-card-title>
      <v-card-text>
        <p>
          A mixin file is an exportable javascript file in which the block of code or functionality to be imported and re-used in your various desired Vue components is defined. Create a Mixins folder inside the src folder and then create a clickMixin.js file inside it. Copy the code below into the newly created file.
          </p>
          <pre>
            <code>// src/mixins/clickMixin.js
export default {
  methods: {
    clicked(value) {
      alert(value);
    }
  }
};</code>
          </pre>
          <br/>
          <p>
            This is the the mixin file, inside is a simple click method that pops up an alert modal. It can be any logic at all, it can have data options, computed properties and anything a Vue component can have.
          </p>
      </v-card-text>
    </v-card>
    <br /><br />

    <v-card>
      <v-card-title>2. Importing the mixin file into components</v-card-title>
    <v-card-text>
    <p>
      Now that a mixin has been created, the next step is to inject it inside the components where it is needed: where the functionality should come to play. In our demonstration above, that would be inside the two components we already created in the beginning of this post. Import the clickMixin inside the two components with the command below:
    </p>
    <pre>
      <code>import clickMixin from ‘../Mixins/clickMixin’</code>
    </pre>
    </v-card-text>
    </v-card>
    <br /><br />

    <v-card>
      <v-card-title>3. Removing repeated logic</v-card-title>
    <v-card-text>
    <p>
      After importing the mixin, you have to remove the logic you had initially as it is now being taken care of by the mixin
    </p>
    </v-card-text>
    </v-card>
    <br /><br />

     <v-card>
      <v-card-title>4. Register the mixin</v-card-title>
    <v-card-text>
    <p>
      This is where you tell the Vue application that what you imported is a mixin and so it should treat the logic inside it as one and the application fixes the functionality and the method calls at the appropriate places in the component. Mixins in Vue by default are registered as arrays like it is below:
    </p>
    <pre>
      <code>&lt;script&gt;
import clickMixin from '../Mixins/clickMixin'
export default {
 name: 'Test',
 mixins: [clickMixin]
}
&lt;/script&gt;</code>
  </pre>
    </v-card-text>
    </v-card>
    <br /><br />

    <h2>Finally the application components should be like it is below:</h2>
    <br/>
    <v-card>
      <v-card-title>Test.vue</v-card-title>
      <v-card-text>
    <pre>
      <code>&lt;template&gt;
  &lt;div&gt;
    &lt;button v-on:click="clicked('From Test Component')"&gt;
      Button 1
    &lt;/button&gt;
  &lt;/div&gt;
&lt;/template&gt;;
&lt;script&gt;
import clickMixin from "../Mixins/clickMixin";
export default {
  name: "Test",
  mixins: [clickMixin]
};
&lt;/script&gt;</code>
  </pre>
    </v-card-text>
    </v-card>
    <br /><br />

    <v-card>
      <v-card-title>Modal.vue</v-card-title>
      <v-card-text>
    <pre>
      <code>&lt;template&gt;
  &lt;div&gt;
    &lt;button v-on:click="clicked('you just clicked on button 2')"&gt;
      Button 2
    &lt;/button&gt;
  &lt;/div&gt;
&lt;/template&gt;;
&lt;script&gt;
import clickMixin from "../Mixins/clickMixin";
export default {
  name: "Modal",
  mixins: [clickMixin]
};
&lt;/script&gt;</code>
  </pre>
    </v-card-text>
    </v-card>
    <br /><br />

    <v-card>
      <v-card-title>App.vue</v-card-title>
      <v-card-text>
    <pre>
      <code>&lt;template&gt;
  &lt;div id="app"&gt;
    &lt;Test /&gt;
    &lt;Modal /&gt;
  &lt;/div&gt;
&lt;/template&gt;;
&lt;script&gt;
import Test from "./components/Test.vue";
import Modal from "./components/Modal.vue";
export default {
  name: "app",
  components: {
    Test,
    Modal
  }
};
&lt;/script&gt;
&lt;style&gt;
&lt;/style&gt;</code>
  </pre>
  <br />
    <v-card-title>Output</v-card-title>
    
      <v-card-text>
      <Test /><br />
    <Modal />
    </v-card-text>
    </v-card-text>
    </v-card>
  </tutorial-layout>
</template>

<script>
import moment from "moment";

import uiView from "@/_mixins/ui-view-mixin";
import tutorialLayout from "../../components/tutorial-layout";
import Test from "./Test.vue";
import Modal from "./Modal.vue";

export default {
  name: "VModel",
  mixins: [uiView],
  created() {},
  data() {
    return {
      text1: "Example Text",
    };
  },
  components: {
    tutorialLayout,
    Test,
    Modal,
  },
  props: {},
  watch: {},
  computed: {
    referenceLinks() {
      return [
        {
          title: "Basics of Mixins",
          href: "https://vuejs.org/v2/guide/mixins.html",
        },
        {
          title: "VueJS - Mixins",
          href: "https://www.tutorialspoint.com/vuejs/vuejs_mixins.htm",
        },
        {
          title: "How to work with Mixins",
          href:
            "https://javascript.plainenglish.io/how-to-work-with-mixins-on-vue-js-a61f72737f25",
        },
      ];
    },
    youtubeLinks() {
      return [
        {
          title: "Learn Mixins",
          href: "https://www.youtube.com/embed/YZuml1Y9BmQ",
        },
        {
          title: "Mixin for novices",
          href: "https://www.youtube.com/embed/BZU6EA5wCp0",
        },
        {
          title: "Mixin VueJs Components",
          href: "https://www.youtube.com/embed/2xpKv3Mt5nw",
        },
      ];
    },
  },
  methods: {},
};
</script>
<style lang="scss">
</style>
